'use client';

import DefaultIllustration from '@/assets/logos/helio/square-dark.svg';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@prismo-io/design-system';
import { useMe } from '@prismo-io/feature-auth/providers/me';
import { Application, ButtonLink, translate } from '@prismo-io/router';
import { HelioResultStatus, HelioTotemCode, LanguageCode, type LanguageCodeT } from '@prismo-io/schemas';
import Image from 'next/image';
import Link from 'next/link';
import { type FC, useMemo } from 'react';
import { Altruism as AltruismTotem } from '../../../assets/totems/altruism';
import { Control as ControlTotem } from '../../../assets/totems/control';
import { Independency as IndependencyTotem } from '../../../assets/totems/independency';
import { Integrity as IntegrityTotem } from '../../../assets/totems/integrity';
import { Pleasure as PleasureTotem } from '../../../assets/totems/pleasure';
import { Protection as ProtectionTotem } from '../../../assets/totems/protection';
import { Security as SecurityTotem } from '../../../assets/totems/security';
import { Universalism as UniversalismTotem } from '../../../assets/totems/universalism';
import type { UserProfileCardValueSchemaT } from '../../../utils/fetch-user-profile-card-data';
import { Countdown } from '../../count-down';
type Props = {
  order: number;
  userUrl: string;
  result: UserProfileCardValueSchemaT;
  canAccessLockedResult: boolean;
};
const buildHelioUrl = (locale: LanguageCodeT) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.HELIO,
    path: '/avant-de-commencer',
    addHostname: true,
    locale
  });
};
export const HelioCard: FC<Props> = (props: Props) => {
  const {
    order,
    userUrl,
    result,
    canAccessLockedResult
  } = props;
  const me = useMe();
  const isMe = useMemo(() => me && me.url === userUrl, [me, userUrl]);
  const isLocked = useMemo(() => result ? result.status === HelioResultStatus.Enum.LOCKED && !canAccessLockedResult : false, [result, canAccessLockedResult]);
  const mainTotem = useMemo(() => isLocked ? undefined : result?.main_totem?.id, [result, isLocked]);
  const totemName = useMemo(() => isLocked ? undefined : result?.main_totem?.translate?.name, [result, isLocked]);
  const TotemIllustation = useMemo(() => {
    switch (mainTotem) {
      case HelioTotemCode.enum.ALTRUISM:
        return AltruismTotem;
      case HelioTotemCode.enum.CONTROL:
        return ControlTotem;
      case HelioTotemCode.enum.INDEPENDENCY:
        return IndependencyTotem;
      case HelioTotemCode.enum.INTEGRITY:
        return IntegrityTotem;
      case HelioTotemCode.enum.PLEASURE:
        return PleasureTotem;
      case HelioTotemCode.enum.PROTECTION:
        return ProtectionTotem;
      case HelioTotemCode.enum.SECURITY:
        return SecurityTotem;
      case HelioTotemCode.enum.UNIVERSALISM:
        return UniversalismTotem;
      default:
        return DefaultIllustration;
    }
  }, [mainTotem]);
  const {
    i18n
  } = useLingui();
  const {
    locale = LanguageCode.Enum.fr
  } = i18n;
  const helioUrl = buildHelioUrl(locale as LanguageCodeT);
  return <Stack component="article" bgcolor={mainTotem ? 'background.primary' : 'white'} borderRadius="3rem" p={2} minWidth={{
    none: '100%',
    sm: '45%',
    lg: '31%'
  }} sx={{
    boxShadow: '2px 2px 7px 0px rgba(0,0,0,0.3)',
    flex: '1 1 0%',
    order: order
  }} direction="column" alignItems="center" spacing={2} data-sentry-element="Stack" data-sentry-component="HelioCard" data-sentry-source-file="index.tsx">
      <Box component="div" position="relative" data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Box component="div" position="relative" width="10.5rem" height="10.5rem" data-sentry-element="Box" data-sentry-source-file="index.tsx">
          {mainTotem ? <TotemIllustation /> : <Image src={DefaultIllustration} alt={t`Motivations Helio | PRISMO`} style={{
          objectFit: 'contain',
          objectPosition: 'center'
        }} fill />}
        </Box>
      </Box>
      {mainTotem && <>
          <Stack direction="row" gap="3px">
            <Typography variant="h3">
              <Trans>Helio</Trans>
            </Typography>
            <Typography variant="h3">
              <Trans>|</Trans>
            </Typography>
            <Typography variant="h3" sx={{
          fontWeight: 'normal'
        }}>
              <Trans>Totem motivationnel :</Trans>
            </Typography>
          </Stack>
          <Box component="div" width="fit-content" marginX="auto" bgcolor="background.primary" borderRadius={1_000_000}>
            <Typography color="primary.main" textAlign="center" paddingY={1} paddingX={2} variant="h4" fontWeight="bold">
              {totemName}
            </Typography>
          </Box>
          <Box component="div">
            <ButtonLink app={Application.APP} path="/profil/:user/motivations-helio" params={{
          user: userUrl
        }} prefetch={false} variant="text" color="inherit" sx={{
          p: 0,
          '& .MuiButton-startIcon': {
            display: 'none'
          }
        }}>
              <Trans>En savoir plus</Trans>
            </ButtonLink>
          </Box>
        </>}
      {!mainTotem && isLocked && isMe && <Typography variant="h3" textAlign="center">
          {result?.unlocked_at instanceof Date ? <Countdown endDate={result.unlocked_at} /> : <Trans>
              Résultats en attente de déblocage par votre administrateur.
            </Trans>}
        </Typography>}
      {!mainTotem && !isLocked && isMe && <>
          <Box component="div">
            <Button LinkComponent={Link} href={helioUrl} variant="contained" color="gradient" sx={{
          '& .MuiButton-startIcon': {
            display: 'none'
          }
        }}>
              <Trans>Passer Helio</Trans>
            </Button>
          </Box>
          <Typography variant="h3">
            <Trans>Identifiez votre totem motivationnel en 8 min !</Trans>
          </Typography>
        </>}
      {!mainTotem && !isMe && <Typography paddingTop={5} variant="h3">
          <Trans>Cet utilisateur n'a pas encore passé Helio</Trans>
        </Typography>}
    </Stack>;
};